import { type MapMode } from "@/features/map/contexts/MapControlContext";

/**
 * 地番検索に使えるデータソースの識別子。
 *
 * 表示レイヤー名ではなく「検索に使うデータの出どころ」を表す。レイヤー名やラベルは
 * UI 側の関心なので本モジュールでは持たず、`searchSourceMeta.ts` が対応付けを持つ。
 */
export type SearchSourceId =
  | "geospaceChiban"
  | "geospaceLegalAffairs"
  | "trustartChiban"
  | "registrationMap"
  | "zenrin";

/**
 * 背景地図の系統。GEOSPACE 系とゼンリン系は規約上重ねられないため、
 * どちらを表示しているかで反対側のデータソースを候補から除外する。
 *
 * `MapMode` と同じ値域だが、意味が「検索の系統」であることを名前で示すために別名にする。
 */
export type MapFamily = MapMode;

/** データソース 1 件の利用可否と表示状態。 */
export interface SearchSourceState {
  /** 契約・フィーチャーフラグ・データ有無から、そもそも使えるか。 */
  available: boolean;
  /** いま地図に表示しているか（Tier の判定に使う）。 */
  visible: boolean;
}

export interface BuildSearchChainParams {
  /** 背景地図の系統。反対側の系統を候補から除外する。 */
  mapFamily: MapFamily;
  /** 各データソースの利用可否と表示状態。 */
  sources: Record<SearchSourceId, SearchSourceState>;
}

/** 検索チェーンの 1 要素。 */
export interface SearchChainEntry {
  id: SearchSourceId;
  /**
   * 1 = 表示中。ヒットしても見えている地図と結果が一致するため乖離が起きない。
   * 2 = 非表示だが利用可能。ヒットしたら呼び出し側が乖離リカバリーを出す。
   */
  tier: 1 | 2;
}

/**
 * データソースが属する系統。排他フィルタの判定に使う。
 *
 * 役所（trustart）系は GEOSPACE ともゼンリンとも排他ではないため、常に候補に残る。
 */
const SOURCE_FAMILY: Record<
  SearchSourceId,
  "geospace" | "trustart" | "zenrin"
> = {
  geospaceChiban: "geospace",
  geospaceLegalAffairs: "geospace",
  trustartChiban: "trustart",
  registrationMap: "trustart",
  zenrin: "zenrin",
};

/**
 * 基本の優先順位。データのカバー範囲とレスポンスの有用性から定義されている。
 *
 * GEOSPACE（地番地図 → 法務省地番地図）→ 役所（地番地図 → 登記所備付地図）→ ゼンリン。
 * この順序は「候補をどう並べるか」だけを決める。叩くかどうかは可用性と排他フィルタが決める。
 */
export const BASE_PRIORITY: readonly SearchSourceId[] = [
  "geospaceChiban",
  "geospaceLegalAffairs",
  "trustartChiban",
  "registrationMap",
  "zenrin",
];

/** すべてのデータソースを「使えない・表示していない」で初期化した状態を返す。 */
export const emptySearchSourceStates = (): Record<
  SearchSourceId,
  SearchSourceState
> => ({
  geospaceChiban: { available: false, visible: false },
  geospaceLegalAffairs: { available: false, visible: false },
  trustartChiban: { available: false, visible: false },
  registrationMap: { available: false, visible: false },
  zenrin: { available: false, visible: false },
});

/**
 * 検索に使うデータソースを優先順に 1 本の列にして返す純粋関数。
 *
 * クリック・登記地番検索・住居表示検索・範囲選択検索の 4 アクションが、この同じ列を
 * 消費する。アクション間の違いは「列の各ソースのどの能力（座標 / 地番 / 住所 / 図形）を
 * 使うか」だけになり、叩く条件そのものはここに一元化される。
 *
 * 3 つの軸の役割を固定しているのが要点:
 * - 契約・フィーチャーフラグ・データ有無 → `available`。候補に入れるかを決める。
 * - `mapFamily` → 反対側の系統を候補から除外するかを決める。
 * - レイヤーの表示状態 → `visible`。Tier（順番）だけを決め、叩くかどうかは決めない。
 *
 * @returns Tier 1（表示中）を基本優先順に並べたあと、Tier 2（非表示）を同じ順に並べた列。
 */
export const buildSearchChain = ({
  mapFamily,
  sources,
}: BuildSearchChainParams): SearchChainEntry[] => {
  // ゼンリン背景のときは GEOSPACE 系を、GEOSPACE 背景のときはゼンリンを候補から外す。
  // 背景が未確定（null）のときはどちらも残す。
  const excludedFamily =
    mapFamily === "zenrin"
      ? "geospace"
      : mapFamily === "geospace"
      ? "zenrin"
      : null;

  const candidates = BASE_PRIORITY.filter((id) => {
    if (!sources[id]?.available) return false;
    return SOURCE_FAMILY[id] !== excludedFamily;
  });

  const tier1 = candidates
    .filter((id) => sources[id].visible)
    .map((id): SearchChainEntry => ({ id, tier: 1 }));
  const tier2 = candidates
    .filter((id) => !sources[id].visible)
    .map((id): SearchChainEntry => ({ id, tier: 2 }));

  return [...tier1, ...tier2];
};
